import { type AuditorTrail } from "../models/audit/attributeTestColumn";
import { type Citation } from "../models/audit/citation";

/**
 * The citation anchored to each cited document, by the document's id.
 *
 * **One resolver, because the two panes read one answer.** The steps pane takes
 * a row's star from the citation on that row's card document and writes the
 * flag back through it; the review pane renders the cards whose citation is
 * starred. Resolved twice, the two spellings are free to pick different
 * citations for one document — and then the star an auditor toggles is not the
 * star the card carries.
 *
 * **The first citation for a document wins, and that is the backend's own
 * rule.** Its anchor map takes the first as well, because a card document is
 * inserted with `on_conflict_do_nothing` on the id: where two renders derive
 * the same card document id, the row the first render wrote is the one that
 * survives and every later one is discarded. The first citation is therefore
 * the one describing the card a reader actually opens, and taking the last
 * would anchor the star to a card that was thrown away.
 *
 * **The map is read both ways.** Its keys are the documents the trail cites at
 * all, which is a different fact from an unstarred one — nothing reaches the
 * workpaper that no citation anchors to — so a missing entry is never taken for
 * a citation with its star off.
 *
 * **The attribute-test path resolves a citation by its id, not by its
 * document.** Where it does group a document's citations, it opens the first
 * for navigation, labels the document with the first's summary, and treats the
 * document as starred if any of them is — reads that path's own display code
 * makes to decide what it presents for a document, which this resolver is never
 * consulted for.
 */
export function citationByDocumentId(
  trail: AuditorTrail | null | undefined,
): ReadonlyMap<string, Citation> {
  const byDocumentId = new Map<string, Citation>();
  for (const step of trail?.steps ?? []) {
    for (const citation of step.citations) {
      if (!byDocumentId.has(citation.documentId)) {
        byDocumentId.set(citation.documentId, citation);
      }
    }
  }
  return byDocumentId;
}
